import { motion, AnimatePresence } from "motion/react";
import { OnboardingProgress } from "./OnboardingProgress";

interface OnboardingLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
  onSkip?: () => void;
  showBack?: boolean;
  showSkip?: boolean;
  skipText?: string;
  isSkipPrimary?: boolean;
}

export function OnboardingLayout({
  children,
  currentStep,
  totalSteps,
  onBack,
  onSkip,
  showBack = true,
  showSkip = true,
  skipText = "Skip",
  isSkipPrimary = false,
}: OnboardingLayoutProps) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-between bg-background px-4 py-8">
      {/* Spacer top */}
      <div />

      {/* Main content — centered */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 24 }}
          animate={{
            opacity: 1,
            x: 0,
            transition: {
              duration: currentStep === 0 ? 0.8 : 0.3,
              ease: [0.16, 1, 0.3, 1],
            },
          }}
          exit={{
            opacity: 0,
            x: -24,
            transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
          }}
          className="w-full max-w-2xl"
        >
          {children}

          {/* Back / Skip links */}
          {(showBack || showSkip) && (
            <div className="mt-8 flex items-center gap-4">
              {showBack && currentStep > 0 && (
                <button
                  onClick={onBack}
                  className="cursor-pointer text-sm font-medium text-foreground/60 transition-colors hover:text-foreground"
                >
                  Back
                </button>
              )}
              {showSkip && (
                <div className="relative flex min-w-[80px] items-center">
                  <AnimatePresence mode="popLayout" initial={false}>
                    {isSkipPrimary ? (
                      <motion.button
                        key="continue"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        onClick={onSkip}
                        className="cursor-pointer rounded-[12px] bg-black px-4 py-1.5 font-medium text-white transition-colors hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                      >
                        {skipText}
                      </motion.button>
                    ) : (
                      <motion.button
                        key="skip"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        onClick={onSkip}
                        className="cursor-pointer py-2 text-sm font-medium text-foreground/60 transition-colors hover:text-foreground inline-flex items-center justify-center"
                      >
                        {skipText}
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Progress dots — bottom */}
      <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />
    </div>
  );
}
